import type { QueryClient } from "@tanstack/react-query"
import axios from "axios"
import i18n from "i18next"
import { toast } from "sonner"
import { TARGET_ROUTES_QUERY_KEY } from "@/api/routes/queryKeys"
import {
  TARGET_DETAILS_QUERY_KEY,
  TARGETS_QUERY_KEY,
} from "@/api/targets/queryKeys"
import { mapTargetSseLocation } from "@/api/targets/targetMappers"
import {
  clearTargetMoveSyncGuard,
  shouldDeferTargetLocationPatchFromSse,
} from "@/api/targets/targetMoveSyncGuard"
import type { Target } from "@/api/targets/targetService"
import type {
  TargetRoute,
  TargetRouteInfo,
  TargetRoutePoint,
} from "@/api/routes/routesService"
import { TARGETS_LIST_QUERY_KEY } from "@/features/TargetsTable/model/useTargets"
import { useStationStore } from "@/stores/stationStore"
import type { Page, TargetListItem } from "@/types"
import {
  setTargetLivePosition,
  removeTargetLivePosition,
} from "@/features/MapWorkspace/model/hooks/target/targetLivePositionStore"

/** Throttle React Query cache patches per target to avoid cascading re-renders at SSE frequency. */
const LOCATION_PATCH_THROTTLE_MS = 500
const lastLocationPatchAt = new Map<string, number>()

export const resetTargetLocationPatchThrottleForTests = () => {
  lastLocationPatchAt.clear()
}

export const patchTargetLocationInCache = (
  queryClient: QueryClient,
  payload: {
    id: string
    lat: number
    lng: number
    edited_at?: string | null
    edited_by?: string | null
  },
) => {
  queryClient.setQueryData<Target[]>([TARGETS_QUERY_KEY], (old = []) =>
    old.map((target) =>
      target.id === payload.id
        ? {
            ...target,
            lat: payload.lat,
            lng: payload.lng,
            edited_at:
              typeof payload.edited_at !== "undefined"
                ? payload.edited_at
                : target.edited_at,
            edited_by:
              typeof payload.edited_by !== "undefined"
                ? payload.edited_by
                : target.edited_by,
          }
        : target,
    ),
  )
  queryClient.setQueryData<Target | null>(
    [TARGET_DETAILS_QUERY_KEY, payload.id],
    (old) =>
      old
        ? {
            ...old,
            lat: payload.lat,
            lng: payload.lng,
            edited_at:
              typeof payload.edited_at !== "undefined"
                ? payload.edited_at
                : old.edited_at,
            edited_by:
              typeof payload.edited_by !== "undefined"
                ? payload.edited_by
                : old.edited_by,
          }
        : old,
  )
}

const toRoutePointFromPayload = (payload: {
  lat: number
  lng: number
  absolute_height?: number | null
  timestamp?: string | null
}): TargetRoutePoint => ({
  lat: payload.lat,
  lng: payload.lng,
  timestamp: payload.timestamp ?? new Date().toISOString(),
  absolute_height:
    typeof payload.absolute_height === "number"
      ? payload.absolute_height
      : null,
})

const sameRoutePoint = (a: TargetRoutePoint, b: TargetRoutePoint): boolean =>
  a.timestamp === b.timestamp &&
  a.lat === b.lat &&
  a.lng === b.lng &&
  a.absolute_height === b.absolute_height

const TERMINAL_TARGET_STATUSES = new Set([
  "false_target",
  "is_lost",
  "suppressed",
  "not_suppressed",
])

const getTargetLifetimeSeconds = (target: Target): number | null => {
  if (!target.created_at) return null

  const createdAt = new Date(target.created_at)
  if (Number.isNaN(createdAt.getTime())) return null

  const statusChangedAt = target.status_changed_at ?? null
  const endAt = statusChangedAt ? new Date(statusChangedAt) : new Date()

  if (Number.isNaN(endAt.getTime())) return null
  return Math.max(0, Math.floor((endAt.getTime() - createdAt.getTime()) / 1000))
}

const mapTargetToRouteInfo = (target: Target): TargetRouteInfo | null => {
  if (!target.id) return null

  return {
    id: target.id,
    name: target.name ?? null,
    status: target.status ?? "created",
    height_above_ground: target.height_above_ground ?? null,
    created_at: target.created_at ?? null,
    status_changed_at:
      target.status && TERMINAL_TARGET_STATUSES.has(target.status)
        ? (target.status_changed_at ?? null)
        : null,
    target_lifetime_seconds:
      target.target_lifetime_seconds ?? getTargetLifetimeSeconds(target),
    params: (target.params ?? []).map((param) => ({
      id: param.id ?? "",
      freq_from: param.freq_from,
      freq_to: param.freq_to,
      bw: param.bw,
      sf: param.sf,
      polarization_angle: param.polarization_angle,
      is_video: param.is_video,
    })),
  }
}

export const patchTargetRouteInCache = (
  queryClient: QueryClient,
  payload: {
    id: string
    lat: number
    lng: number
    absolute_height?: number | null
    timestamp?: string | null
    status?: string | null
    target?: TargetRouteInfo | null
  },
) => {
  const point = toRoutePointFromPayload(payload)
  const routeQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: TARGET_ROUTES_QUERY_KEY })

  routeQueries.forEach((query) => {
    const queryTargetStatus =
      typeof query.queryKey[5] === "string" ? query.queryKey[5] : null

    if (
      queryTargetStatus &&
      (!payload.status || payload.status !== queryTargetStatus)
    ) {
      return
    }

    queryClient.setQueryData<TargetRoute[]>(query.queryKey, (old = []) => {
      const existingRoute = old.find((route) => route.target_id === payload.id)

      if (!existingRoute) {
        return [
          ...old,
          {
            target_id: payload.id,
            points: [point],
            first_point: point,
            last_point: point,
            target: payload.target ?? null,
          },
        ]
      }

      const nextPoints = existingRoute.points.some((item) =>
        sameRoutePoint(item, point),
      )
        ? existingRoute.points
        : [...existingRoute.points, point].sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
          )

      return old.map((route) =>
        route.target_id === payload.id
          ? {
              ...route,
              points: nextPoints,
              first_point: nextPoints[0] ?? null,
              last_point: nextPoints[nextPoints.length - 1] ?? null,
              target: route.target ?? payload.target ?? null,
            }
          : route,
      )
    })
  })
}

const getCachedTargetStatus = (
  queryClient: QueryClient,
  targetId: string,
): string | null => {
  const targets = queryClient.getQueryData<Target[]>([TARGETS_QUERY_KEY])
  const target = targets?.find((item) => item.id === targetId)
  return target?.status ?? null
}

const getCachedTargetRouteInfo = (
  queryClient: QueryClient,
  targetId: string,
): TargetRouteInfo | null => {
  const targets = queryClient.getQueryData<Target[]>([TARGETS_QUERY_KEY])
  const target = targets?.find((item) => item.id === targetId)
  return target ? mapTargetToRouteInfo(target) : null
}

const toOptionalFiniteNumber = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined

export const patchTargetTtlInCache = (
  queryClient: QueryClient,
  payload: {
    target_id: string
    ex?: number | null
    expires_at?: number | null
    ttl_remaining_seconds?: number | null
  },
) => {
  queryClient.setQueryData<Target[]>([TARGETS_QUERY_KEY], (old = []) => {
    let changed = false
    const next = old.map((target) => {
      if (target.id !== payload.target_id) {
        return target
      }
      changed = true
      return {
        ...target,
        ...(payload.ex !== undefined ? { ex: payload.ex ?? undefined } : {}),
        ...(payload.expires_at !== undefined
          ? { expires_at: payload.expires_at ?? undefined }
          : {}),
        ...(payload.ttl_remaining_seconds !== undefined
          ? {
              ttl_remaining_seconds: payload.ttl_remaining_seconds ?? undefined,
            }
          : {}),
      }
    })
    return changed ? next : old
  })
}

export const removeLiveTargetFromCaches = (
  queryClient: QueryClient,
  targetId: string,
) => {
  clearTargetMoveSyncGuard(targetId)
  removeTargetLivePosition(targetId)
  lastLocationPatchAt.delete(targetId)
  useStationStore.getState().removeTargetFromAllRelations(targetId)
  void queryClient.cancelQueries({ queryKey: [TARGETS_QUERY_KEY] })
  void queryClient.cancelQueries({ queryKey: [TARGETS_LIST_QUERY_KEY] })
  void queryClient.cancelQueries({
    queryKey: [TARGET_DETAILS_QUERY_KEY, targetId],
  })
  queryClient.removeQueries({
    queryKey: [TARGET_DETAILS_QUERY_KEY, targetId],
  })
  queryClient.setQueryData<Target[]>([TARGETS_QUERY_KEY], (old = []) =>
    old.filter((target) => target.id !== targetId),
  )
  queryClient.setQueriesData<Page<TargetListItem>>(
    { queryKey: [TARGETS_LIST_QUERY_KEY] },
    (old) => {
      if (!old) {
        return old
      }

      const items = old.items.filter((target) => target.id !== targetId)
      if (items.length === old.items.length) {
        return old
      }

      const total = Math.max(0, old.total - 1)
      return {
        ...old,
        items,
        total,
        pages: old.size > 0 ? Math.ceil(total / old.size) : old.pages,
      }
    },
  )
  void queryClient.invalidateQueries({ queryKey: TARGET_ROUTES_QUERY_KEY })
}

export const applyKeepaliveHttpError = (
  queryClient: QueryClient,
  targetId: string,
  error: unknown,
) => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  if (status === 404) {
    removeLiveTargetFromCaches(queryClient, targetId)
    return
  }

  toast.error(
    i18n.t("target_ttl.extend_failed", {
      defaultValue: "Could not extend target lifetime",
    }),
  )
}

export const applyTargetSseMessageToCache = (
  queryClient: QueryClient,
  message: {
    key?: string
    identifier?: string
    data?: any
  } | null,
) => {
  if (message?.key === "target_action") {
    const action = message.data?.action
    if (!["created", "updated", "deleted"].includes(action)) {
      return false
    }
    const targetId = message.identifier || message.data?.data?.id

    if (targetId) {
      if (action === "deleted") {
        removeLiveTargetFromCaches(queryClient, targetId)
      } else {
        void queryClient.invalidateQueries({ queryKey: [TARGETS_QUERY_KEY] })
        void queryClient.invalidateQueries({
          queryKey: [TARGETS_LIST_QUERY_KEY],
        })
        void queryClient.invalidateQueries({
          queryKey: [TARGET_DETAILS_QUERY_KEY, targetId],
        })
        void queryClient.invalidateQueries({
          queryKey: TARGET_ROUTES_QUERY_KEY,
        })
      }
    } else {
      void queryClient.invalidateQueries({ queryKey: [TARGETS_QUERY_KEY] })
      void queryClient.invalidateQueries({ queryKey: [TARGETS_LIST_QUERY_KEY] })
      void queryClient.invalidateQueries({ queryKey: TARGET_ROUTES_QUERY_KEY })
    }

    return true
  }

  if (message?.key === "target_ttl") {
    const payload = message.data ?? {}
    const targetId = payload.target_id || message.identifier || payload.id
    if (targetId) {
      patchTargetTtlInCache(queryClient, {
        target_id: String(targetId),
        ex: toOptionalFiniteNumber(payload.ex),
        expires_at: toOptionalFiniteNumber(payload.expires_at),
        ttl_remaining_seconds: toOptionalFiniteNumber(
          payload.ttl_remaining_seconds,
        ),
      })
    }
    return true
  }

  const targetLocation = mapTargetSseLocation(message)
  if (!targetLocation) {
    return false
  }

  const isDeferredByGuard = shouldDeferTargetLocationPatchFromSse(
    targetLocation.id,
  )

  if (!isDeferredByGuard) {
    const locationPayload =
      message?.data?.location && typeof message.data.location === "object"
        ? message.data.location
        : message?.data
    const absoluteHeight =
      typeof locationPayload?.absolute_height === "number"
        ? locationPayload.absolute_height
        : undefined

    // TargetMarker reads live position with a per-ID subscription so only the
    // moved marker re-renders. Skip updates while the local drag/settle guard is
    // active so stale SSE coordinates do not flash over the drag preview.
    setTargetLivePosition(targetLocation.id, {
      lat: targetLocation.lat,
      lng: targetLocation.lng,
      ...(absoluteHeight !== undefined ? { absoluteHeight } : {}),
    })
  }

  if (!isDeferredByGuard) {
    const now = Date.now()
    const lastPatch = lastLocationPatchAt.get(targetLocation.id) ?? 0
    if (now - lastPatch >= LOCATION_PATCH_THROTTLE_MS) {
      lastLocationPatchAt.set(targetLocation.id, now)
      patchTargetLocationInCache(queryClient, targetLocation)
    }
  }

  if (message?.data?.location?.add_target_history !== false) {
    patchTargetRouteInCache(queryClient, {
      ...targetLocation,
      status: getCachedTargetStatus(queryClient, targetLocation.id),
      target: getCachedTargetRouteInfo(queryClient, targetLocation.id),
      absolute_height:
        typeof message?.data?.location?.absolute_height === "number"
          ? message.data.location.absolute_height
          : null,
      timestamp:
        typeof message?.data?.timestamp === "string"
          ? message.data.timestamp
          : null,
    })
  }
  return true
}
